import type { CycleEvaluatorCriteriaTypeValue } from '@/constants/cycle-evaluation/CycleEvaluatorCriteria';
import type {
    CycleEvaluatorCriteria,
    CycleEvaluatorSelection,
} from '@/types/cycle-evaluation/CycleEvaluatorSelection';
import type { PriorityAttribute } from '@/types/priorityAttributeType';
import type { DraggableItem } from '@amelio/ui-library';

const PRIORITY_LEVELS = [1, 2, 3] as const;

type CriteriaIdToTypeMap = Map<string, string>;

interface PriorityItems {
    priority1: DraggableItem<PriorityAttribute>[];
    priority2: DraggableItem<PriorityAttribute>[];
    priority3: DraggableItem<PriorityAttribute>[];
}

interface GetCriteriaTypeParams {
    item: DraggableItem<PriorityAttribute>;
    criteriaIdToTypeMap: CriteriaIdToTypeMap;
}

const getCriteriaType = ({ item, criteriaIdToTypeMap }: GetCriteriaTypeParams): string =>
    criteriaIdToTypeMap.get(item.id) || item.id;

interface BuildCycleEvaluatorCriteriasParams {
    priorityItems: PriorityItems;
    cycleEvaluatorSelectionId: string;
    criteriaIdToTypeMap: CriteriaIdToTypeMap;
}

const buildCycleEvaluatorCriterias = ({
    priorityItems,
    cycleEvaluatorSelectionId,
    criteriaIdToTypeMap,
}: BuildCycleEvaluatorCriteriasParams): CycleEvaluatorCriteria[] => {
    const tiers = [priorityItems.priority1, priorityItems.priority2, priorityItems.priority3];

    return PRIORITY_LEVELS.flatMap((priority, tierIndex) =>
        tiers[tierIndex].map((item, index) => ({
            id: criteriaIdToTypeMap.has(item.id) ? item.id : '',
            cycleEvaluatorSelectionId,
            type: getCriteriaType({ item, criteriaIdToTypeMap }) as CycleEvaluatorCriteriaTypeValue,
            priority,
            sortOrder: index,
        })),
    );
};

type SelectionContent = Pick<
    CycleEvaluatorSelection,
    'cycleEvaluatorCriterias' | 'suggestedReviewerCount'
>;

// Reads each tier the way hydration fills it, so an untouched form matches what the server holds.
const getSelectionSignature = ({
    cycleEvaluatorCriterias,
    suggestedReviewerCount,
}: SelectionContent): string =>
    [
        suggestedReviewerCount,
        ...PRIORITY_LEVELS.map((priority) =>
            cycleEvaluatorCriterias
                .filter((criteria) => criteria.priority === priority)
                .map((criteria) => criteria.type)
                .join(','),
        ),
    ].join('|');

export { buildCycleEvaluatorCriterias, getCriteriaType, getSelectionSignature };
export type { PriorityItems, SelectionContent };
